import { Profile } from 'src/app/shared/entities/profile';
import {
  ExtendedKeyUsagesFormValue,
  extendedKeyUsagesFormValueFromDto,
  extendedKeyUsagesFormValueToDto,
} from 'src/app/shared/form-field/components/form-fields/extended-key-usage-form-field/extended-key-usage-form.types';
import {
  KeyUsagesFormValue,
  keyUsagesFormValueFromDto,
  keyUsagesFormValueToDto,
} from 'src/app/shared/form-field/components/form-fields/key-usage-form-field/key-usage-form.types';
import { CreateCertificateProfileRequestDto } from 'src/app/shared/services/api/dto/create-certificate-profile-request-dto';
import { UpdateProfileRequestDto } from 'src/app/shared/services/api/dto/update-profile-request-dto';
import { parseIntWithDefaultNull } from 'src/app/shared/util/parse-int-with-default-null';

export enum ProfileFormField {
  profileName = 'profileName',
  maxCertificateValidityDays = 'maxCertificateValidityDays',
  maxCertificateValidityMonths = 'maxCertificateValidityMonths',
  maxCertificateValidityYears = 'maxCertificateValidityYears',
  allowExtensionOverride = 'allowExtensionOverride',
  overridableExtensionOIDs = 'overridableExtensionOIDs',
  criticalBasicConstraints = 'criticalBasicConstraints',
  keyUsages = 'keyUsages',
  extendedKeyUsages = 'extendedKeyUsages',
  crlDistributionEndpoint = 'crlDistributionEndpoint',
  useLastToFirstDnOrder = 'useLastToFirstDnOrder',
  ca = 'ca',
  pathLengthConstraint = 'pathLengthConstraint',
  profileType = 'profileType',
  criticalCRLDistributionPoint = 'criticalCRLDistributionPoint',
  ocspServiceLocator = 'ocspServiceLocator',
}

export interface ProfileFormValue {
  [ProfileFormField.profileName]: string | null;
  [ProfileFormField.maxCertificateValidityDays]: number | null;
  [ProfileFormField.maxCertificateValidityMonths]: number | null;
  [ProfileFormField.maxCertificateValidityYears]: number | null;
  [ProfileFormField.allowExtensionOverride]: boolean | null;
  [ProfileFormField.overridableExtensionOIDs]: (string | null)[] | undefined;
  [ProfileFormField.criticalBasicConstraints]: boolean | null;
  [ProfileFormField.keyUsages]: KeyUsagesFormValue;
  [ProfileFormField.extendedKeyUsages]: ExtendedKeyUsagesFormValue;
  [ProfileFormField.crlDistributionEndpoint]: string | null;
  [ProfileFormField.useLastToFirstDnOrder]: boolean | null;
  [ProfileFormField.ca]: string | null;
  [ProfileFormField.pathLengthConstraint]: number | null;
  [ProfileFormField.profileType]: string | null;
  [ProfileFormField.criticalCRLDistributionPoint]: boolean | null;
  [ProfileFormField.ocspServiceLocator]: string | null;
}

export const PROFILE_FORM_EDITABLE_FIELD = [
  ProfileFormField.maxCertificateValidityDays,
  ProfileFormField.maxCertificateValidityMonths,
  ProfileFormField.maxCertificateValidityYears,
  ProfileFormField.allowExtensionOverride,
  ProfileFormField.overridableExtensionOIDs,
  ProfileFormField.criticalBasicConstraints,
  ProfileFormField.keyUsages,
  ProfileFormField.extendedKeyUsages,
  ProfileFormField.crlDistributionEndpoint,
  ProfileFormField.useLastToFirstDnOrder,
  ProfileFormField.ca,
  ProfileFormField.pathLengthConstraint,
  ProfileFormField.profileType,
  ProfileFormField.criticalCRLDistributionPoint,
  ProfileFormField.ocspServiceLocator,
];

const DAYS_IN_MONTHS = 30;
const DAYS_IN_YEAR = 365;

function certificateValidityUnitsToDays({
  days,
  months,
  years,
}: {
  days: number;
  months: number;
  years: number;
}): number {
  return days + months * DAYS_IN_MONTHS + years * DAYS_IN_YEAR;
}

export function profileFormValueToCreateRequestDto(
  formValue: Partial<ProfileFormValue>,
): CreateCertificateProfileRequestDto {
  if (!formValue[ProfileFormField.profileName]) {
    throw new Error('Validation should have prevented this: empty profileName');
  }
  if (!formValue[ProfileFormField.profileType]) {
    throw new Error('Validation should have prevented this: empty profileType');
  }
  const keyUsagesDto = keyUsagesFormValueToDto(
    formValue[ProfileFormField.keyUsages],
  );
  const extendedKeyUsagesDto = extendedKeyUsagesFormValueToDto(
    formValue[ProfileFormField.extendedKeyUsages],
  );
  return {
    profileName: formValue[ProfileFormField.profileName],
    maxCertificateValidityInDays: certificateValidityUnitsToDays({
      days:
        parseIntWithDefaultNull(
          formValue[ProfileFormField.maxCertificateValidityDays],
        ) || 0,
      months:
        parseIntWithDefaultNull(
          formValue[ProfileFormField.maxCertificateValidityMonths],
        ) || 0,
      years:
        parseIntWithDefaultNull(
          formValue[ProfileFormField.maxCertificateValidityYears],
        ) || 0,
    }),
    allowExtensionOverride:
      !!formValue[ProfileFormField.allowExtensionOverride],
    overridableExtensionOIDs:
      formValue[ProfileFormField.overridableExtensionOIDs]?.flatMap(item =>
        item ? [item] : [],
      ) ?? [],
    criticalBasicConstraints:
      !!formValue[ProfileFormField.criticalBasicConstraints],
    ...keyUsagesDto,
    ...extendedKeyUsagesDto,
    crlDistributionEndpoint:
      formValue[ProfileFormField.crlDistributionEndpoint] || null,
    criticalCRLDistributionPoint:
      !!formValue[ProfileFormField.criticalCRLDistributionPoint],
    useLastToFirstDnOrder: !!formValue[ProfileFormField.useLastToFirstDnOrder],
    ca: formValue[ProfileFormField.ca] || null,
    pathLengthConstraint: parseIntWithDefaultNull(
      formValue[ProfileFormField.pathLengthConstraint],
    ),
    profileType: formValue[ProfileFormField.profileType],
    ocspServiceLocator: formValue[ProfileFormField.ocspServiceLocator] || null,
  };
}

export function profileToFormValue(profile: Profile): ProfileFormValue {
  return {
    [ProfileFormField.profileName]: profile.profileName,
    [ProfileFormField.maxCertificateValidityDays]:
      profile.maxCertificateValidityInDays,
    [ProfileFormField.maxCertificateValidityMonths]: null,
    [ProfileFormField.maxCertificateValidityYears]: null,
    [ProfileFormField.allowExtensionOverride]: profile.allowExtensionOverride,
    [ProfileFormField.overridableExtensionOIDs]:
      profile.overridableExtensionOIDs ?? undefined,
    [ProfileFormField.criticalBasicConstraints]:
      profile.criticalBasicConstraints,
    [ProfileFormField.keyUsages]: keyUsagesFormValueFromDto(profile),
    [ProfileFormField.extendedKeyUsages]:
      extendedKeyUsagesFormValueFromDto(profile),
    [ProfileFormField.crlDistributionEndpoint]: profile.crlDistributionEndpoint,
    [ProfileFormField.useLastToFirstDnOrder]: profile.useLastToFirstDnOrder,
    [ProfileFormField.ca]: profile.ca,
    [ProfileFormField.pathLengthConstraint]: profile.pathLengthConstraint,
    [ProfileFormField.profileType]: profile.profileType,
    [ProfileFormField.criticalCRLDistributionPoint]:
      !!profile.criticalCRLDistributionPoint,
    [ProfileFormField.ocspServiceLocator]: profile.ocspServiceLocator,
  };
}

function nonEmptyArrayOrNull<T>(array: T[]): T[] | null {
  return array.length > 0 ? array : null;
}

export function profileFormValueToUpdateRequestDto(
  formValue: Partial<ProfileFormValue>,
): UpdateProfileRequestDto {
  // Instead of an empty arrays, null is to be sent
  // https://ssc-xpki.slack.com/archives/C06MH1AA1JP/p1747767580862299?thread_ts=1747758451.523339&cid=C06MH1AA1JP
  const keyUsagesDto = keyUsagesFormValueToDto(
    formValue[ProfileFormField.keyUsages],
  );
  const keyUsages = nonEmptyArrayOrNull(keyUsagesDto.keyUsages);
  const extendedKeyUsagesDto = extendedKeyUsagesFormValueToDto(
    formValue[ProfileFormField.extendedKeyUsages],
  );
  const extendedKeyUsages = nonEmptyArrayOrNull(
    extendedKeyUsagesDto.extendedKeyUsages,
  );
  const overridableExtensionOIDsArr =
    formValue[ProfileFormField.overridableExtensionOIDs]?.flatMap(item =>
      item ? [item] : [],
    ) ?? [];
  const overridableExtensionOIDs = nonEmptyArrayOrNull(
    overridableExtensionOIDsArr,
  );
  return {
    maxCertificateValidityInDays: certificateValidityUnitsToDays({
      days:
        parseIntWithDefaultNull(
          formValue[ProfileFormField.maxCertificateValidityDays],
        ) || 0,
      months:
        parseIntWithDefaultNull(
          formValue[ProfileFormField.maxCertificateValidityMonths],
        ) || 0,
      years:
        parseIntWithDefaultNull(
          formValue[ProfileFormField.maxCertificateValidityYears],
        ) || 0,
    }),
    allowExtensionOverride:
      !!formValue[ProfileFormField.allowExtensionOverride],
    overridableExtensionOIDs,
    criticalBasicConstraints:
      !!formValue[ProfileFormField.criticalBasicConstraints],
    ...keyUsagesDto,
    keyUsages,
    ...extendedKeyUsagesDto,
    extendedKeyUsages,
    crlDistributionEndpoint:
      formValue[ProfileFormField.crlDistributionEndpoint] || null,
    useLastToFirstDnOrder: !!formValue[ProfileFormField.useLastToFirstDnOrder],
    // ca: formValue[ProfileFormField.ca] || null,
    pathLengthConstraint: parseIntWithDefaultNull(
      formValue[ProfileFormField.pathLengthConstraint],
    ),
    profileType: formValue[ProfileFormField.profileType] || null,
    ca: formValue[ProfileFormField.ca] || null,
    criticalCRLDistributionPoint:
      !!formValue[ProfileFormField.criticalCRLDistributionPoint],
    ocspServiceLocator: formValue[ProfileFormField.ocspServiceLocator] || null,
  };
}
